/**
 * Automation URL Parameters
 *
 * A standalone, minimally-coupled module that reads URL search parameters and
 * applies them to the fire simulation.  It is intentionally framework-agnostic
 * so that it can be ported to simkit-core with minimal changes.
 *
 * Supported parameters
 * --------------------
 * | Param       | Values                                      | Example            |
 * |-------------|---------------------------------------------|--------------------|
 * | windSpeed   | none | light | moderate | strong  (or 0–4) | windSpeed=strong   |
 * | fireDanger  | low | medium | high | veryhigh | severe  | fireDanger=high    |
 * | windDir     | N | NE | E | SE | S | SW | W | NW           | windDir=SW         |
 * | ignition    | x,y  (repeatable)                           | ignition=50,30     |
 * | autoStart   | 1 | true | yes                              | autoStart=1        |
 *
 * Example URLs
 * ------------
 * Basic auto-start with defaults:
 *   ?ignition=50,30&autoStart=1
 *
 * Full scenario:
 *   ?windSpeed=strong&fireDanger=high&windDir=NE&ignition=80,60&autoStart=1
 *
 * Multiple ignition points:
 *   ?windSpeed=moderate&fireDanger=veryhigh&ignition=40,40&ignition=120,90&autoStart=1
 *
 * Screenshot / e2e prep (no autoStart, just pre-configure state):
 *   ?windSpeed=light&fireDanger=medium&windDir=S&ignition=60,50
 */

import {
    WindSpeed,
    FireDanger,
    WindDirections,
    isWindDirection,
    type FireCell,
    type Plane,
    type TerrainBase,
    type Controls,
} from '@firesim/simkit-core';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface IgnitionCoord {
    x: number;
    y: number;
}

export interface AutomationParams {
    /** Desired wind speed, or null if not specified. */
    windSpeed: WindSpeed | null;
    /** Desired fire danger level, or null if not specified. */
    fireDanger: FireDanger | null;
    /** Wind FROM direction (user-facing convention), or null if not specified. */
    windDirection: WindDirections | null;
    /** Grid cell coordinates to ignite, in order. */
    ignitions: IgnitionCoord[];
    /** Whether to start the simulation immediately after applying params. */
    autoStart: boolean;
}

/**
 * Context required to apply automation params to a running simulation.
 * Callbacks are optional so callers only provide what they support.
 *
 * The generic signature mirrors the simkit-core Controls/Plane API so this
 * interface can be moved to simkit-core unchanged.
 */
export interface AutomationTarget<TC extends TerrainBase, C extends FireCell<TC>> {
    controls: Controls<TC, C>;
    plane: Plane<TC, C>;
    /**
     * Called once per ignition coordinate.  Provide this callback from the
     * darwin-specific `placeIgnitionAt` helper (or any future equivalent).
     */
    placeIgnition?: (coord: IgnitionCoord, plane: Plane<TC, C>) => void;
    /**
     * Called when autoStart or ignitions are present, to close/hide any
     * blocking dialogs before the simulation begins.
     */
    dismissDialogs?: () => void;
}

// ---------------------------------------------------------------------------
// Internal lookup tables
// ---------------------------------------------------------------------------

// Typed as partial so lookups for unknown keys return `undefined` rather than
// the base type, allowing safe `?? null` coalescing with meaningful type checks.
const WIND_SPEED_MAP: Readonly<Partial<Record<string, WindSpeed>>> = {
    none:     WindSpeed.None,
    light:    WindSpeed.Light,
    moderate: WindSpeed.Moderate,
    strong:   WindSpeed.Strong,
    '0':      WindSpeed.None,
    '1':      WindSpeed.None,
    '2':      WindSpeed.Light,
    '3':      WindSpeed.Moderate,
    '4':      WindSpeed.Strong,
};

const FIRE_DANGER_MAP: Readonly<Partial<Record<string, FireDanger>>> = {
    low:      FireDanger.Low,
    medium:   FireDanger.Medium,
    high:     FireDanger.High,
    veryhigh: FireDanger.VeryHigh,
    severe:   FireDanger.Severe,
};

// ---------------------------------------------------------------------------
// Parse
// ---------------------------------------------------------------------------

/**
 * Parse automation URL parameters from a query string.
 *
 * @param search - Query string to parse (defaults to `window.location.search`).
 *                 Accepts both `?key=val` and plain `key=val` forms.
 * @returns Structured {@link AutomationParams} object.  Fields are null / empty
 *          when the corresponding parameter is absent or invalid.
 */
export function parseAutomationParams(search?: string): AutomationParams {
    const queryString = search ?? (
        typeof window !== 'undefined' ? window.location.search : ''
    );
    const params = new URLSearchParams(queryString);

    // --- windSpeed ---
    let windSpeed: WindSpeed | null = null;
    const wsRaw = params.get('windSpeed');
    if (wsRaw !== null) {
        windSpeed = WIND_SPEED_MAP[wsRaw.toLowerCase()] ?? null;
        if (windSpeed === null) {
            console.warn(`[url-params] Unknown windSpeed value: "${wsRaw}". Valid values: none, light, moderate, strong (or 0-4).`);
        }
    }

    // --- fireDanger ---
    let fireDanger: FireDanger | null = null;
    const fdRaw = params.get('fireDanger');
    if (fdRaw !== null) {
        fireDanger = FIRE_DANGER_MAP[fdRaw.toLowerCase()] ?? null;
        if (fireDanger === null) {
            console.warn(`[url-params] Unknown fireDanger value: "${fdRaw}". Valid values: low, medium, high, veryhigh, severe.`);
        }
    }

    // --- windDir ---
    // Accepts the FROM direction as shown in the UI (N, NE, E, SE, S, SW, W, NW).
    let windDirection: WindDirections | null = null;
    const wdRaw = params.get('windDir');
    if (wdRaw !== null) {
        const upper = wdRaw.toUpperCase() as WindDirections;
        if (isWindDirection(upper)) {
            windDirection = upper;
        } else {
            console.warn(`[url-params] Unknown windDir value: "${wdRaw}". Valid values: N, NE, E, SE, S, SW, W, NW.`);
        }
    }

    // --- ignition (repeatable: ?ignition=x,y&ignition=x2,y2) ---
    const ignitions: IgnitionCoord[] = params.getAll('ignition').flatMap((raw) => {
        const [xStr, yStr] = raw.split(',');
        const x = parseInt(xStr, 10);
        const y = parseInt(yStr, 10);
        if (isNaN(x) || isNaN(y)) {
            console.warn(`[url-params] Invalid ignition coordinate: "${raw}". Expected "x,y" integer pair.`);
            return [];
        }
        return [{ x, y }];
    });

    // --- autoStart ---
    const autoStartRaw = params.get('autoStart');
    const autoStart = autoStartRaw !== null &&
        ['1', 'true', 'yes'].includes(autoStartRaw.toLowerCase());

    return { windSpeed, fireDanger, windDirection, ignitions, autoStart };
}

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

/**
 * Apply parsed automation params to a live simulation.
 *
 * Execution order:
 *  1. Set wind speed, fire danger, and wind direction on controls.
 *  2. Place ignitions via the provided callback.
 *  3. Dismiss blocking dialogs when autoStart is set.
 *  4. Start the plane when autoStart is true.
 *
 * @param params  - Output of {@link parseAutomationParams}.
 * @param target  - Simulation objects and optional callbacks.
 */
export function applyAutomationParams<TC extends TerrainBase, C extends FireCell<TC>>(
    params: AutomationParams,
    target: AutomationTarget<TC, C>,
): void {
    const { controls, plane, placeIgnition, dismissDialogs } = target;

    if (params.windSpeed !== null) {
        controls.windSpeed = params.windSpeed;
    }

    if (params.fireDanger !== null) {
        controls.fireDanger = params.fireDanger;
    }

    if (params.windDirection !== null) {
        controls.windDirection = params.windDirection;
    }

    if (params.ignitions.length > 0 && placeIgnition) {
        for (const coord of params.ignitions) {
            try {
                placeIgnition(coord, plane);
            } catch (err) {
                console.warn(`[url-params] Failed to place ignition at (${coord.x},${coord.y}):`, err);
            }
        }
    }

    // Dismiss blocking dialogs only when autoStart is requested.
    // For pre-config-only URLs (e.g. ignitions without autoStart), keep the
    // intro dialog behavior unchanged.
    // Keep this in a try/catch so dialog DOM issues cannot block autoStart.
    if (params.autoStart && dismissDialogs) {
        try {
            dismissDialogs();
        } catch (err) {
            console.warn('[url-params] Failed to dismiss dialogs:', err);
        }
    }

    if (params.autoStart) {
        plane.start();
    }
}
